(function () {
  'use strict';

  const OBSERVER_OPTIONS = { rootMargin: '0px 0px 400px 0px' };
  const CACHE_PREFIX = 'plp-related-searches-v1:';
  const inMemoryCache = new Map();

  function sanitizeHtml(value) {
    return String(value || '').replace(/<[^>]*>/g, '').trim();
  }

  function cacheKey(handle, query) {
    return `${handle}::${query}`;
  }

  function getCached(key) {
    if (inMemoryCache.has(key)) {
      return inMemoryCache.get(key);
    }

    try {
      const raw = sessionStorage.getItem(CACHE_PREFIX + key);
      if (raw) {
        const parsed = JSON.parse(raw);
        inMemoryCache.set(key, parsed);
        return parsed;
      }
    } catch (_error) {
      // noop
    }

    return null;
  }

  function setCached(key, value) {
    inMemoryCache.set(key, value);
    try {
      sessionStorage.setItem(CACHE_PREFIX + key, JSON.stringify(value));
    } catch (_error) {
      // noop
    }
  }

  function buildRows(items, rowSize) {
    const rows = [];
    for (let i = 0; i < items.length; i += rowSize) {
      rows.push(items.slice(i, i + rowSize));
    }
    return rows;
  }

  function renderLinks(target, items) {
    const linksWrap = target.querySelector('.collection-seo-interlinking__related-links');
    if (!linksWrap) return;

    linksWrap.innerHTML = '';
    if (!items.length) {
      target.hidden = true;
      return;
    }

    const rows = buildRows(items, 3);
    rows.forEach((row) => {
      const rowEl = document.createElement('div');
      rowEl.className = 'collection-seo-interlinking__link-row';

      row.forEach((item, index) => {
        if (index > 0) {
          const separator = document.createElement('span');
          separator.className = 'collection-seo-interlinking__separator';
          separator.setAttribute('aria-hidden', 'true');
          separator.textContent = '|';
          rowEl.appendChild(separator);
        }

        const link = document.createElement('a');
        link.className = 'collection-seo-interlinking__link';
        link.href = item.url;
        link.textContent = item.label;
        rowEl.appendChild(link);
      });

      linksWrap.appendChild(rowEl);
    });

    target.hidden = false;
  }

  function parseSuggestJson(data, maxItems) {
    const queries = data?.resources?.results?.queries || [];
    return queries
      .map((queryItem) => {
        const label = sanitizeHtml(queryItem.styled_text || queryItem.text);
        return {
          label,
          url: queryItem.url || `/search?q=${encodeURIComponent(label)}`,
        };
      })
      .filter((item) => item.label && item.url)
      .slice(0, maxItems);
  }

  function parsePredictiveFallbackHtml(markup, maxItems) {
    const doc = new DOMParser().parseFromString(markup, 'text/html');
    const anchors = doc.querySelectorAll('.predictive-search-results__card--query a');
    const parsed = [];

    anchors.forEach((anchor) => {
      const label = sanitizeHtml(anchor.textContent || '');
      const href = anchor.getAttribute('href') || '';
      if (!label || !href) return;
      parsed.push({ label, url: href });
    });

    return parsed.slice(0, maxItems);
  }

  async function fetchSuggest(query, maxItems, signal) {
    const url = new URL((window.Theme?.routes?.predictive_search_url || '/search/suggest') + '.json', window.location.origin);
    url.searchParams.set('q', query);
    url.searchParams.append('resources[type]', 'query');
    url.searchParams.append('resources[type]', 'product');
    url.searchParams.set('resources[limit]', '30');
    url.searchParams.set('resources[limit_scope]', 'each');

    const response = await fetch(url.toString(), { signal, priority: 'low' });
    if (!response.ok) return [];
    const data = await response.json();
    return parseSuggestJson(data, maxItems);
  }

  async function fetchFallbackMarkup(query, sectionId, maxItems, signal) {
    const url = new URL(window.Theme?.routes?.predictive_search_url || '/search/suggest', window.location.origin);
    url.searchParams.set('q', query);
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('section_id', sectionId);

    const response = await fetch(url.toString(), { signal, priority: 'low' });
    if (!response.ok) return [];
    const markup = await response.text();
    return parsePredictiveFallbackHtml(markup, maxItems);
  }

  async function loadRelatedSearches(block) {
    const shell = block.querySelector('[data-related-searches-shell]');
    if (!(shell instanceof HTMLElement)) return;

    if (shell.dataset.hasOverride === 'true' || shell.dataset.loaded === 'true') return;

    const query = (shell.dataset.query || '').trim();
    const handle = (shell.dataset.collectionHandle || '').trim();
    const maxItems = Math.max(10, Math.min(15, Number(shell.dataset.maxItems || 12)));
    const fallbackSectionId = shell.dataset.fallbackSectionId || 'predictive-search';

    if (!query) {
      block.hidden = true;
      shell.dataset.loaded = 'true';
      return;
    }

    const key = cacheKey(handle, query);
    const cached = getCached(key);
    if (cached) {
      renderLinks(shell, cached);
      shell.dataset.loaded = 'true';
      return;
    }

    const controller = new AbortController();
    shell._abortController = controller;

    try {
      let items = await fetchSuggest(query, maxItems, controller.signal);

      if (!items.length) {
        items = await fetchFallbackMarkup(query, fallbackSectionId, maxItems, controller.signal);
      }

      if (!items.length) {
        block.hidden = true;
      } else {
        setCached(key, items);
        renderLinks(shell, items);
      }
    } catch (error) {
      if (error?.name !== 'AbortError') {
        block.hidden = true;
      }
    } finally {
      shell.dataset.loaded = 'true';
      delete shell._abortController;
    }
  }

  function init() {
    const blocks = document.querySelectorAll('[data-related-searches-block]');
    if (!blocks.length) return;

    const observer = new IntersectionObserver((entries, io) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        io.unobserve(entry.target);
        loadRelatedSearches(entry.target);
      });
    }, OBSERVER_OPTIONS);

    blocks.forEach((block) => observer.observe(block));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
